import { AccessRegistryQuery, GetEmploymentAccessQuery, GetRolesQuery } from "@/graphql/generated/graphql";
import { createContext } from "react";

export interface EmployeeListItem {
  id: string;
  fullName: string;
  imageUrl?: string | null;
}

export type AccessRegistryEntities = AccessRegistryQuery["accessRegistry"];
export type EmploymentAccess = NonNullable<GetEmploymentAccessQuery["getEmploymentAccess"]>;
export type CompanyRole = GetRolesQuery["getRoles"][number];

/**
 * Draft state of one grid cell (action × entity) for the selected employment.
 * projectIds null = company-wide; a non-empty list = scoped to those projects.
 * validTill null = permanent. A cell with no draft entry is not granted
 * directly (it may still come from a role — see roleGrantSources).
 */
export interface CellDraft {
  projectIds: string[] | null;
  validTill: string | null;
}

export const cellKey = (action: string, entity: string) => `${action}:${entity}`;

export interface PermissionMatrixContextValue {
  // employees
  employeeList: EmployeeListItem[];
  employeeListLoading: boolean;

  // registry (static)
  registry: AccessRegistryEntities | null;
  registryLoading: boolean;

  // selection
  selectedEmploymentId: string | null;
  selectedEmployee: EmployeeListItem | null;
  selectEmployment: (employmentId: string) => void;
  clearSelection: () => void;

  // selected employment's access
  selectedAccess: EmploymentAccess | null;
  selectedPermissionsLoading: boolean;
  /** cellKey -> names of assigned roles granting that pair (read-only in the grid) */
  roleGrantSources: Map<string, string[]>;

  // draft grant editing (direct grants only)
  draft: Map<string, CellDraft>;
  toggleCell: (action: string, entity: string) => void;
  setColumn: (action: string, entityKeys: string[], checked: boolean) => void;
  setCellScope: (action: string, entity: string, projectIds: string[] | null) => void;
  setCellExpiry: (action: string, entity: string, validTill: string | null) => void;
  resetDraft: () => void;
  saveDraft: () => Promise<void>;
  saving: boolean;
  isDirty: boolean;

  // roles
  companyRoles: CompanyRole[];
  rolesLoading: boolean;
  assignRoleTo: (employmentId: string, roleId: string) => Promise<void>;
  unassignRoleFrom: (employmentId: string, roleId: string) => Promise<void>;
  refetchRoles: () => void;

  // project picker data (for scoping cells)
  projectOptions: { id: string; title: string; code: string }[];
}

const PermissionMatrixContext = createContext<PermissionMatrixContextValue>({
  employeeList: [],
  employeeListLoading: false,

  registry: null,
  registryLoading: false,

  selectedEmploymentId: null,
  selectedEmployee: null,
  selectEmployment: () => { },
  clearSelection: () => { },

  selectedAccess: null,
  selectedPermissionsLoading: false,
  roleGrantSources: new Map(),

  draft: new Map(),
  toggleCell: () => { },
  setColumn: () => { },
  setCellScope: () => { },
  setCellExpiry: () => { },
  resetDraft: () => { },
  saveDraft: async () => { },
  saving: false,
  isDirty: false,

  companyRoles: [],
  rolesLoading: false,
  assignRoleTo: async () => { },
  unassignRoleFrom: async () => { },
  refetchRoles: () => { },

  projectOptions: [],
});

export default PermissionMatrixContext
